import { store, store$ } from '@redux/store';
import { filter, fromEvent, map, merge, take } from 'rxjs';
import { segmentPageView, segmentTrack, segmentIdentify } from 'features/segment';
import typeahead from "features/articleSearchBox";
import HousingProductType from 'constants/housingProductType';
import { selectModal, selectFetchingModal } from 'layout/redux/selectors';

const getNavigatorItemElement = (event) => event.target.closest('[data-navigator-item]');
const getLinkableArticleElement = (event) => event.target.closest('[data-article-card-link]');
const getLinkableLifestyleInsightElement = (event) => event.target.closest(
    '[data-section-name="lifestyleInsights"] [data-sublanding-link]');
const isMetroSpecificSection = (event) => !!event.target.closest('[data-section-name="metroSpecificSubjects"]');
const isCommunityStandardCard = (dataset) => dataset.type === HousingProductType.Community().getName();
const isPlanStandardCard = (dataset) => dataset.type === HousingProductType.Plan().getName();
const isSpecStandardCard = (dataset) => dataset.type === HousingProductType.Spec().getName();
const isMostPopularSection = (event) => !!event.target.closest('[data-section-name="mostPopularArticles"]');
const isMostRecentSection = (event) => !!event.target.closest('[data-section-name="mostRecentArticles"]');
const isWeAlsoRecommendSection = (event) => !!event.target.closest('[data-section-name="weAlsoRecommendArticles"]');
const getStandardCardElement = (event) => event.target.closest('[data-card]');
const isListingForYouSection = (event) => !!event.target.closest('[data-listings-for-you-container]');
const isLinkableListingElement = (event) => event.target instanceof HTMLAnchorElement;
const isFavoriteIcon = (event) => !!event.target.closest('.icon__save');
const isCallToAction = (event) => !!event.target.closest('[data-lead-generator-opener]');
const isStandardCard = (event) => !!event.target.closest('[data-standard-card]');

const bodyClick$ = fromEvent(document.body, 'click');
const bodyMouseDown$ = fromEvent(document.body, 'mousedown');
const bodyTouchEnd$ = fromEvent(document.body, 'touchend');
const listingForYouSectionClick$ =  bodyClick$.pipe(filter(event => isListingForYouSection(event)));
const onWindowLoad$ = fromEvent(window, 'load');

/**
 * triggerPageViewEvent: Dispatch the page view on window load.
 */
const triggerPageViewEvent = function() {
    const customProperties = {
        page_title: 'landing',
        page_url: window.location.href,
        content_type: 'landing',
        section: 'Learning Center',
    };
    store.dispatch(segmentPageView({customProperties}));
};

onWindowLoad$.subscribe(triggerPageViewEvent);

/**
 * Dispatches a Segment Event When an suggestion is clicked.
 * @param suggestion - is the suggestion clicked in the search box.
 */
const triggerSearchExecutedEvent = function(suggestion) {
    const customProperties = {
        search_value: suggestion.dataset.title,
        pagetype_content_group: 'Learning Center',
    };

    store.dispatch(segmentTrack({ eventName: 'Search_Executed', customProperties }));
}

typeahead
  .getSuggestionListClickedObserver()
  .subscribe(triggerSearchExecutedEvent);

/**
 * Dispatches a Segment Event event when a navigator item is clicked.
 * @param item - is the item clicked in navigator section.
 */
const triggerNavigatorItemClick = function(item) {
  const customProperties = {
    page_type: 'Learning_Center',
    tag_text: item.dataset.text,
    destination_url: item.dataset.url,
  };

  store.dispatch(segmentTrack({ eventName: 'Tag_Clicked', customProperties, skipDebounce: true }));
};

// Observers to navigator item section Events Logic
const onNavigatorItemClick$ = merge(bodyClick$, bodyMouseDown$, bodyTouchEnd$).pipe(
  map(getNavigatorItemElement),
  filter(Boolean),
  take(1),
);

onNavigatorItemClick$.subscribe(triggerNavigatorItemClick);

/**
 * The getCardImageUrls function takes a cardElement as an argument and returns an object with the following properties:
 * primaryImage, secondaryImage, tertiaryImage.
 * if there are no images in the cardElement then each property will be assigned to an empty string.
 * This can be used to other events in the list view section.
 * @param cardElement Get the image elements from the card element
 *
 * @return An object with primaryImage, secondaryImage, tertiaryImage.
 */
const getCardImageUrls = (cardElement) => {
    const imageElements = cardElement.querySelectorAll('img');
  
    if (imageElements) {
      return {
        primaryImage: imageElements[0]?.src || '',
        secondaryImage: imageElements[1]?.src || '',
        tertiaryImage: imageElements[2]?.src || '',
      };
    }
  
    return {};
};

/**
 * getLeadButtonClickedPaidCommunityCustomProperties: function to get the custom properties of lead button click
 * based on the community listing card information
 * @param {HTMLElement} standardCommunityCard dom element of the listing card
 * @returns custom properties object
 */
const getLeadButtonClickedPaidCommunityCustomProperties = function(standardCommunityCard){

    const leadButton = standardCommunityCard.querySelector('[data-lead-generator-opener]');
    const leadButtonDataset = leadButton.dataset || {};
    const cardDataset = standardCommunityCard.dataset;

    const customProperties = {
        market_id: cardDataset.marketId,
        market_name: cardDataset.marketName,
        city: cardDataset.city,
        state: cardDataset.stateAbbreviation,
        zip: cardDataset.zip,
        builder_id: cardDataset.builderId,
        builder_name: cardDataset.brandName,
        community_id: cardDataset.id,
        community_name: cardDataset.communityName,
        community_status: cardDataset.marketingStatusLabel,
        primary_image_url: cardDataset.imageUrl,
        community_low_price: cardDataset.priceLow,
        community_high_price: cardDataset.priceHigh,
        button_action: leadButtonDataset.buttonAction,
        page_type: 'Learning_Center',
      };

    return customProperties;
}

/**
 * getLeadButtonClickedCallToActionPaidPlanCustomProperties: function to get the custom properties of lead button click
 * based on the plan listing card information
 * @param {HTMLElement} standardPlanCard dom element of the listing card
 * @returns custom properties object
 */
const getLeadButtonClickedPaidPlanCustomProperties = function(standardPlanCard){

    const leadButton = standardPlanCard.querySelector('[data-lead-generator-opener]');
    const leadButtonDataset = leadButton.dataset || {};
    const cardDataset = standardPlanCard.dataset;
  
    const primaryImageUrl =
      cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(standardPlanCard).primaryImage;
  
    const customProperties = {
      market_id: cardDataset.marketId,
      market_name: cardDataset.marketName,
      city: cardDataset.city,
      state: cardDataset.stateAbbreviation,
      zip: cardDataset.zip,
      builder_id: cardDataset.builderId,
      builder_name: cardDataset.brandName,
      community_id: cardDataset.communityId,
      community_name: cardDataset.communityName,
      primary_image_url: primaryImageUrl,
      button_action: leadButtonDataset.buttonAction,
      plan_id: cardDataset.planId,
      home_type: cardDataset.homeStatus,
      home_price: cardDataset.price,
      page_type: 'Learning_Center',
    };

    return customProperties;
}

/**
 * getLeadButtonClickedCallToActionPaidSpecCustomProperties: function to get the custom properties of lead button click
 * based on the spec listing card information
 * @param {HTMLElement} standardSpecCard dom element of the listing card
 * @returns custom properties object
 */
const getLeadButtonClickedPaidSpecCustomProperties = function(standardSpecCard){
    const leadButton = standardSpecCard.querySelector('[data-lead-generator-opener]');
    const leadButtonDataset = leadButton.dataset || {};
    const cardDataset = standardSpecCard.dataset;
  
    const primaryImageUrl =
      cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(standardSpecCard).primaryImage;
  
    const customProperties = {
      market_id: cardDataset.marketId,
      market_name: cardDataset.marketName,
      city: cardDataset.city,
      state: cardDataset.stateAbbreviation,
      zip: cardDataset.zip,
      builder_id: cardDataset.builderId,
      builder_name: cardDataset.brandName,
      community_id: cardDataset.communityId,
      community_name: cardDataset.communityName,
      primary_image_url: primaryImageUrl,
      button_action: leadButtonDataset.buttonAction,
      spec_id: cardDataset.specId,
      home_type: cardDataset.homeStatus,
      home_price: cardDataset.price,
      page_type: 'Learning_Center',
    };

    return customProperties;
}

/**
 * triggerPaidSpecModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Free Brochure
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerPaidSpecModalSubmitted(modalInformation) {
    const { listingInformation } = modalInformation;
    const customProperties = {
      builder_id: listingInformation.builderId,
      builder_name: listingInformation.brandName,
      button_action: modalInformation.buttonAction,
      city: listingInformation.city,
      community_id: listingInformation.id,
      community_name: listingInformation.communityName,
      home_price: listingInformation.price,
      home_type: listingInformation.homeStatus,
      market_id: listingInformation.marketId,
      market_name: listingInformation.marketName,
      primary_image_url: listingInformation.imageUrl,
      state: listingInformation.stateAbbreviation,
      spec_id: listingInformation.specId,
      zip: listingInformation.zip,
    };
  
    return customProperties;
  }
  
  /**
   * triggerPaidCommunityModalSubmitted: Triggers the event when the modal form is submitted.
   * This event only applies for the following forms:
   * - Free Brochure
   * - Get More Information
   * @param {object} modalInformation The information related to the modal opened.
   */
  function triggerPaidCommunityModalSubmitted(modalInformation) {
    const { listingInformation } = modalInformation;
    const customProperties = {
      market_id: listingInformation.marketId,
      market_name: listingInformation.marketName,
      builder_id: listingInformation.builderId,
      builder_name: listingInformation.brandName,
      community_id: listingInformation.id,
      community_name: listingInformation.communityName,
      community_status: listingInformation.marketingStatusLabel,
      primary_image_url: listingInformation.imageUrl,
      community_low_price: listingInformation.priceLow,
      community_high_price: listingInformation.priceHigh,
      city: listingInformation.city,
      state: listingInformation.stateAbbreviation,
      zip: listingInformation.zip,
      button_action: modalInformation.buttonAction,
    };
  
    return customProperties;
  }

/**
 * triggerPaidPlanModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Free Brochure
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerPaidPlanModalSubmitted(modalInformation) {
    const { listingInformation } = modalInformation;
    const customProperties = {
      builder_id: listingInformation.builderId,
      builder_name: listingInformation.brandName,
      button_action: modalInformation.buttonAction,
      city: listingInformation.city,
      community_id: listingInformation.id,
      community_name: listingInformation.communityName,
      home_price: listingInformation.price,
      home_type: listingInformation.homeStatus,
      market_id: listingInformation.marketId,
      market_name: listingInformation.marketName,
      plan_id: listingInformation.planId,
      primary_image_url: listingInformation.imageUrl,
      state: listingInformation.stateAbbreviation,
      zip: listingInformation.zip,
    };
  
    return customProperties;
  }

/**
 * Dispatches a Segment Event When the submitt button is clicked.
 * @param customProperties - is the custom properties of the event.
 */
const triggerModalSubmittedEvent = function(customProperties) {
    store.dispatch(segmentTrack({ eventName: 'Lead Modal Form Submitted', customProperties }));
  }
  
  const triggerIdentifyModalSubmission = () => {
    store.dispatch(segmentIdentify({ eventName: 'Lead Modal Form Submitted' }));
  };

/**
 * Dispatches a Segment Event When the call to action is clicked.
 * @param customProperties - is the custom properties of the event.
 */
const triggerLeadButtonClickedEvent = function(customProperties) {
    store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties }));
}

/**
 * Dispatches a Segment Event event when a sub landing page card is clicked.
 * @param subLanding - is the sublanding clicked in Lifestyle Insights section.
 */
const triggerLifestyleInsightClick = function(subLanding) {
    const customProperties = {
      page_type: 'Learning_Center',
      click_target: subLanding.dataset.title,
      destination: subLanding.dataset.url,
      position: subLanding.dataset.index,
    };
  
    store.dispatch(segmentTrack({ eventName: 'sub_landing_page', customProperties }));
};


/**
 * Dispatches a Segment Event event recommendation article is clicked.
 * @param article - is the article clicked in recommendation articles section.
 */
const triggerRecomendedArticlesClick = function(article) {
    const customProperties = {
      recommendation_type: 'similar articles',
      content_id: article.dataset.slug,
      content_title: article.dataset.title
    };
  
    store.dispatch(segmentTrack({ eventName: 'Recommendation_clicked', customProperties }));
};

/**
 * Dispatches a Segment Event When an article is clicked.
 * @param article - is the article clicked in the articles section.
 */
const triggerArticleClick = function(article) {
    const customProperties = {
        article_id: article.dataset.slug,
        article_title: article.dataset.title
    };

    store.dispatch(segmentTrack({ eventName: 'Article_Clicked', customProperties }));
};

/**
 * Dispatches a Segment Event When a listing is clicked.
 * @param data - listing information needed.
 */
const triggerListingClick = function(data) {
    const customProperties = {
        listing_id: data.card.dataset.id,
        interaction_type: data.interactionType,
        listing_name : data.card.dataset.name
    };

    store.dispatch(segmentTrack({ eventName: 'Listing_Interacted', customProperties }));
};

// Observers to Life Style Section Events Logic
const onLifestyleInsightClick$ = bodyClick$.pipe(
    map(getLinkableLifestyleInsightElement),
    filter(Boolean)
);

onLifestyleInsightClick$.subscribe(triggerLifestyleInsightClick);

// Observers to Most Popular Section Events Logic
const onMostPopularArticlesClick$ = bodyClick$.pipe(
    filter(isMostPopularSection),
    map(getLinkableArticleElement),
    filter(Boolean)
);
  
onMostPopularArticlesClick$.subscribe(triggerRecomendedArticlesClick);

// Observers to Most Recent Section Events Logic
const onMostRecentArticlesClick$ = bodyClick$.pipe(
    filter(isMostRecentSection),
    map(getLinkableArticleElement),
    filter(Boolean)
);
  
onMostRecentArticlesClick$.subscribe(triggerRecomendedArticlesClick);

// Observers to We Also Recommend Section Section Events Logic
const onWeAlsoRecommendArticlesClick$ = bodyClick$.pipe(
    filter(isWeAlsoRecommendSection),
    map(getLinkableArticleElement),
    filter(Boolean)
);
  
onWeAlsoRecommendArticlesClick$.subscribe(triggerRecomendedArticlesClick);

// Observers to Metro Specific Section Events Logic
const onMetroSpecificArticleClick$ = bodyClick$.pipe(
    filter(isMetroSpecificSection),
    map(getLinkableArticleElement),
    filter(Boolean)
);

onMetroSpecificArticleClick$.subscribe(triggerArticleClick);

// Observers to Lead Button Clicked Event Logic
const callToActionStandardCardClick$ = listingForYouSectionClick$.pipe(
    filter(isCallToAction),
    map(getStandardCardElement),
    filter(Boolean),
);
const callToActionSpecClick$ = callToActionStandardCardClick$.pipe(
    filter((standardCard) => isSpecStandardCard(standardCard.dataset)),
    map(getLeadButtonClickedPaidSpecCustomProperties)
);

const callToActionPlanClick$ = callToActionStandardCardClick$.pipe(
    filter((standardCard) => isPlanStandardCard(standardCard.dataset)),
    map(getLeadButtonClickedPaidPlanCustomProperties)
);

const callToActionCommunityClick$ = callToActionStandardCardClick$.pipe(
    filter((standardCard) => isCommunityStandardCard(standardCard.dataset)),
    map(getLeadButtonClickedPaidCommunityCustomProperties)
);

merge(
    callToActionSpecClick$,callToActionPlanClick$,callToActionCommunityClick$
).subscribe(triggerLeadButtonClickedEvent);

// Observers to Listing For You Section Events Logic
const cardClick$ = listingForYouSectionClick$.pipe(
    filter(event => isStandardCard(event) && !isFavoriteIcon(event) &&
    !isLinkableListingElement(event) && !isCallToAction(event)),
    map(event => ({
        interactionType: 'card_click',
        card: getStandardCardElement(event),
    }))
);

const favoriteIconClick$ = listingForYouSectionClick$.pipe(
    filter(event => isFavoriteIcon(event)),
    map(event => ({
        interactionType: 'favorite',
        card: getStandardCardElement(event),
    }))
);

const linkableListingElementClick$ = listingForYouSectionClick$.pipe(
    filter(event => isLinkableListingElement(event)),
    map(event => ({
        interactionType: 'link_click',
        card: getStandardCardElement(event),
    }))
);

merge(cardClick$,favoriteIconClick$,linkableListingElementClick$).subscribe(triggerListingClick);

// Observers to ModalSubmitted Event Logic
const modalSubmittedClick$ = store$.pipe(
    filter(selectFetchingModal),
    map(selectModal),
    filter(Boolean),
    filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  );
  
  const modalSubmittedSpecClick$ = modalSubmittedClick$.pipe(
    filter((modalInformation) => isSpecStandardCard(modalInformation.listingInformation)),
    map(triggerPaidSpecModalSubmitted)
  );
  
  const modalSubmittedPlanClick$ = modalSubmittedClick$.pipe(
    filter((modalInformation) => isPlanStandardCard(modalInformation.listingInformation)),
    map(triggerPaidPlanModalSubmitted)
  );
  
  const modalSubmittedCommunityClick$ = modalSubmittedClick$.pipe(
    filter((modalInformation) => isCommunityStandardCard(modalInformation.listingInformation)),
    map(triggerPaidCommunityModalSubmitted)
  );
  
  merge(
    modalSubmittedSpecClick$,modalSubmittedPlanClick$,modalSubmittedCommunityClick$
  ).subscribe((customProperties) => {
    triggerModalSubmittedEvent(customProperties)
    triggerIdentifyModalSubmission()
  });